import type { CreatePriceRequest } from '@domains/subscription/priv/Billables/api/api'
import {
  type AssignedPriceMapper,
  mapAssignedPaymentFrequency,
  mapPaymentFrequencyToPriceFrequency,
  type PriceRequestMapper,
} from '@domains/subscription/priv/Billables/api/mappers'
import type { BillablePrice } from '@domains/subscription/priv/types'
import type {
  AssignedPriceAlgorithm,
  LinearAlgorithm,
  TemplateAssignedPrice,
  TemplatePriceRequest,
} from '@mynt/backoffice-api-spec/billables'
import type { LinearPrices } from './LinearPriceForm'
import type { LinearPrice, LinearPriceRequest } from './linear'

export const isLinear = (billable: unknown): billable is LinearPrice => {
  return (billable as LinearPrice)?.type === 'Linear'
}

export const isLinearAssignedPrice = (price: TemplateAssignedPrice): price is LinearAssignedPrice => {
  return price.algorithm.type === 'Linear'
}

export const mapBillableToLinearPriceForm = <T extends BillablePrice>(billable: T | undefined): LinearPrices => {
  if (!billable || !isLinear(billable)) {
    return {
      constants: [],
      coefficients: [],
      paymentFrequency: undefined,
    }
  }

  return {
    constants: billable.constants ? billable.constants.map((c) => ({ value: c.value, currency: c.currency })) : [],
    coefficients: billable.coefficients ? billable.coefficients.map((c) => ({ value: c.value, currency: c.currency })) : [],
    paymentFrequency: billable.paymentFrequency,
  }
}

export class linearPriceRequestMapper implements PriceRequestMapper {
  supports(price: CreatePriceRequest): price is LinearPriceRequest {
    return price.type === 'Linear'
  }

  toAssignedPriceAlgorithm(price: CreatePriceRequest): AssignedPriceAlgorithm {
    if (!this.supports(price)) throw new Error(`Unsupported price request type in linearPriceRequestMapper: ${price.type}`)

    return {
      type: 'Linear',
      coefficients: price.coefficients ?? [],
      constants: price.constants ?? [],
    }
  }

  toPriceRequestDto(price: CreatePriceRequest): TemplatePriceRequest {
    if (!this.supports(price)) throw new Error(`Unsupported price request type in linearPriceRequestMapper: ${price.type}`)

    return {
      algorithm: this.toAssignedPriceAlgorithm(price),
      priceFrequency: mapPaymentFrequencyToPriceFrequency(price.paymentFrequency),
    }
  }
}

type LinearAssignedPrice = TemplateAssignedPrice & {
  algorithm: LinearAlgorithm
}

export class LinearAssignedPriceMapper implements AssignedPriceMapper {
  supports(assignedPrice: TemplateAssignedPrice) {
    return isLinearAssignedPrice(assignedPrice)
  }

  toBillablePrice(assignedPrice: TemplateAssignedPrice): LinearPrice {
    if (!isLinearAssignedPrice(assignedPrice))
      throw new Error(`Unsupported assigned price type in LinearAssignedPriceMapper: ${assignedPrice.algorithm.type}`)

    return {
      billableId: assignedPrice.billableId,
      type: 'Linear',
      coefficients: assignedPrice.algorithm.coefficients,
      constants: assignedPrice.algorithm.constants,
      paymentFrequency: mapAssignedPaymentFrequency(assignedPrice.priceFrequency),
      supported: true,
    }
  }
}
